import { useEffect, useRef, useState } from 'react';
import { Blog, Country, TravelItinerary } from 'src/types';
import { AppRoutes } from 'src/lib/routes';
import ImageWithFallback from 'src/components/ImageWithFallback';
import Link from 'next/link';
import { HEADER_HEIGHT } from 'src/lib/constants';
import Slider from 'react-slick';
import HeroImage from 'public/images/blog/hero.png';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import BlogCardV2 from './BlogCardV2';
import ReadAllBlog from 'src/components/blog/ReadAllBlog';
import IlluImage from 'public/images/illu/illu_13.png';
import { truncate } from 'lodash-es';
import classNames from 'classnames';
import { scrollTo } from 'src/helpers';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/BreadcrumbItem';
import Banner from 'src/components/Banner';
import { useTranslations } from 'next-intl';

type Props = {
  country?: Country | null;
  countries: Country[];
  featuredBlog: Blog | null;
  mostReadBlogs: Blog[];
  travelItineraries: TravelItinerary[];
  blogsCount: number;
};

const BlogLayout: React.FC<Props> = ({
  country: showingCountry,
  countries,
  featuredBlog,
  mostReadBlogs,
  travelItineraries,
  blogsCount
}) => {
  const translation = useTranslations('components.BlogLayout')
  const countryNavRef = useRef<HTMLDivElement>(null);
  const blogsBodyRef = useRef<HTMLDivElement>(null);
  const [leftAlignCountryNav, setLeftAlignCountryNav] = useState(false);

  useEffect(() => {
    if (countryNavRef.current) {
      setLeftAlignCountryNav(countryNavRef.current.scrollWidth > countryNavRef.current.clientWidth);
    }
  }, []);

  return (
    <>
      <div className="tw-bg-brand-green tw-py-16 md:tw-py-24">
        <div className="tw-text-center">
          {showingCountry && (
            <Breadcrumb className="tw-justify-center tw-mb-8 tw-pl-3.75">
              <BreadcrumbItem>
                <Link legacyBehavior prefetch={false} href={AppRoutes.rootRoute({}).toPath()}>
                  {translation('home')}
                </Link>
              </BreadcrumbItem>
              <BreadcrumbItem>
                <a href={ AppRoutes.countryRoute({ id: showingCountry.slug }).toPath()}>
                  {showingCountry.name}
                </a>
              </BreadcrumbItem>
            </Breadcrumb>
          )}

          <ImageWithFallback
            src={HeroImage}
            alt=""
            className="tw-inline-block tw-static tw-object-contain tw-h-40 md:tw-h-72 tw-w-full"
          />
        </div>

        <div className="tw-text-center tw-my-8 tw-px-20 md:tw-px-0 tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none tw-font-semibold tw-font-styleSans">
          {translation('yourInsiderGuide')}
        </div>

        <div className="container">
          <div className="tw-py-4 tw-border-t-2 tw-border-b-2 tw-border-white">
            <div
              ref={countryNavRef}
              className={classNames('tw-flex tw-items-center tw-overflow-x-auto custom-scrollbar', {
                'md:tw-justify-center': !leftAlignCountryNav,
              })}
            >
              {countries.map((country, index) => (
                  <div className="tw-flex tw-items-center" key={country.id}>
                    {index > 0 ? (
                      <div className="tw-mx-4 tw-font-semibold tw-text-brand-navy">|</div>
                    ) : null}
                    <div
                      className={classNames({
                        'tw-pb-0.5 md:tw-pb-1.5': showingCountry?.id === country.id,
                      })}
                    >
                      <Link legacyBehavior prefetch={false} href={AppRoutes.cateBlogsRoute({ id: country.slug }).toPath()}>
                        <a
                          className={classNames(
                            'tw-text-brand-navy tw-font-styleSans tw-font-semibold',
                            {
                              'tw-text-base md:tw-text-2xl tw-underline':
                                showingCountry?.id === country.id,
                              'tw-text-sm md:tw-text-base': showingCountry?.id !== country.id,
                            }
                          )}
                        >
                          {country.name}
                        </a>
                      </Link>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {featuredBlog ? (
          <div className="container tw-mt-16 md:tw-my-16">
            <Link legacyBehavior prefetch={false} href={AppRoutes.shortMagazineRoute({ id: featuredBlog.slug }).toPath()}>
              <a className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-between">
                <div className="tw-flex-shrink-0 tw-text-center md:tw-w-1/2 md:tw-px-20 tw-order-2 md:tw-order-1">
                  <div className="tw-text-brand-navy tw-font-semibold tw-font-styleSans tw-text-5xl md:tw-text-6xl tw-leading-none md:tw-leading-none tw-text-center tw-mb-4 md:tw-mb-8 tw-underline">
                    {featuredBlog.title}
                  </div>
                  <div className="tw-text-brand-navy">{featuredBlog.excerpt}</div>
                </div>
                <div className="md:tw-w-1/2 tw-relative md:tw-order-2 tw-order-1 tw-mb-4 md:tw-mb-0">
                  <div className="tw--top-5 tw-left-1/2 md:tw--left-10 tw-transform md:tw-transform-none tw--translate-x-1/2 tw-absolute tw-bg-purple tw-px-8 tw-py-3 tw-uppercase tw-font-semibold tw-rounded-4xl tw-text-brand-navy tw-whitespace-nowrap">
                    {featuredBlog.topicName}
                  </div>
                  <ImageWithFallback
                    src={featuredBlog.coverImgUrl}
                    alt={featuredBlog.title}
                    fill
                    className="tw-flex-shrink-0 tw-block tw-static tw-object-cover tw-h-80 md:tw-h-96 tw-w-full tw-rounded-lg"
                    unoptimized={true}
                  />
                </div>
              </a>
            </Link>
          </div>
        ) : null}

        {mostReadBlogs.length > 0 ? (
          <div className="container tw-pt-12 md:tw-pt-16">
            <div className="tw-py-4 tw-border-t-2 tw-border-b-2 tw-border-white tw-text-center tw-text-3xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-font-semibold tw-font-styleSans tw-text-brand-navy tw-mb-12 md:tw-mb-16">
              {translation('featuredGuides')}
            </div>

            <Slider
              prevArrow={
                <div className="slick-prev">
                  <HiOutlineArrowNarrowLeft strokeWidth={2} />
                </div>
              }
              nextArrow={
                <div className="slick-next">
                  <HiOutlineArrowNarrowRight strokeWidth={2} />
                </div>
              }
              infinite={false}
              slidesToShow={3}
              slidesToScroll={1}
              arrows={true}
              dots={false}
              responsive={[
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 1,
                    arrows: false,
                    dots: true,
                  },
                },
              ]}
              className="slider-v2 carousel-featured-guide tw--mx-2 md:tw--mx-4"
            >
              {mostReadBlogs.map((blog) => (
                <div key={blog.id} className="tw-px-2 md:tw-px-4 tw-flex-shrink-0">
                  <BlogCardV2 blog={blog} />
                </div>
              ))}
            </Slider>
          </div>
        ) : null}
      </div>

      {travelItineraries.length > 0 ? (
        <div className="tw-bg-brand-yellow tw-py-16 md:tw-py-24">
          <div className="container">
            <div className="row tw-items-center tw-pb-10 md:tw-pb-0">
              <div className="col-md-4 tw-text-center tw-text-brand-navy tw-px-12 tw-mb-8 md:tw-mb-0">
                <ImageWithFallback
                  src={IlluImage}
                  alt=""
                  className="tw-inline-block tw-static tw-h-auto tw-w-32 md:tw-w-40 tw-mb-8 md:tw-mb-4"
                />
                <div className="tw-font-semibold tw-font-styleSans tw-text-5xl md:tw-text-6xl tw-leading-none md:tw-leading-none tw-mb-2 md:tw-mb-4">
                  {translation('itinerary')}
                  <br />
                  {translation('ideas')}
                </div>
                <div>{translation('useThese')}</div>
              </div>
              <div className="col-md-8">
                <Slider
                  slidesToShow={2}
                  slidesToScroll={1}
                  infinite={false}
                  arrows={travelItineraries.length > 2}
                  className="slider-v2 carousel-itineraries"
                  responsive={[
                    {
                      breakpoint: 800,
                      settings: {
                        slidesToShow: 1,
                        arrows: travelItineraries.length > 1
                      },
                    },
                  ]}
                  prevArrow={
                    <div className="slick-prev">
                      <HiOutlineArrowNarrowLeft
                        size={24}
                        strokeWidth={2}
                        className="tw-text-brand-navy"
                      />
                    </div>
                  }
                  nextArrow={
                    <div className="slick-next">
                      <HiOutlineArrowNarrowRight
                        size={24}
                        strokeWidth={2}
                        className="tw-text-brand-navy"
                      />
                    </div>
                  }
                >
                  {travelItineraries.map((itinerary) => (
                    <div key={itinerary.id} className="tw-px-4">
                      <Link legacyBehavior prefetch={false} href={AppRoutes.itineraryRoute({ id: itinerary.slug }).toPath()}>
                        <a className="tw-block">
                          <ImageWithFallback
                            src={itinerary.image?.webpMdUrl}
                            alt={itinerary.title}
                            fill
                            className="tw-flex-shrink-0 tw-block tw-static tw-object-cover tw-h-60 tw-w-full tw-rounded-tr-lg tw-rounded-tl-lg"
                            unoptimized={true}
                          />
                          <div className="tw-p-6 tw-text-center tw-font-semibold tw-text-white tw-bg-brand-navy tw-rounded-br-lg tw-rounded-bl-lg">
                            <div className="tw-uppercase tw-mb-4 tw-text-xs md:tw-text-sm">
                              {translation('curatedItinerary')}
                            </div>
                            <div className="tw-leading-none md:tw-leading-none tw-text-xl md:tw-text-2xl tw-mb-2 tw-h-10 md:tw-h-12 tw-font-styleSans">
                              {truncate(itinerary.title, { length: 40 })}
                            </div>
                            <div>
                              <HiOutlineArrowNarrowRight size={28} strokeWidth={2} />
                            </div>
                          </div>
                        </a>
                      </Link>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div ref={blogsBodyRef} className="tw-py-16 md:tw-py-24">
        <div className="container">
          <h2 className="tw-text-center tw-font-styleSans tw-font-semibold tw-text-2xl md:tw-text-4xl tw-leading-none md:tw-leading-none tw-mb-12 md:tw-mb-16">
            {translation('readAll')}
          </h2>

          <ReadAllBlog
            country={showingCountry}
            countries={countries}
            blogsCount={blogsCount}
            onPageChange={() => {
              if (blogsBodyRef.current) {
                scrollTo(blogsBodyRef.current, -HEADER_HEIGHT);
              }
            }}
          />
        </div>
      </div>

      {showingCountry ? (
        <Banner
          title={translation('sellAllExperience', {name: showingCountry.name})}
          image={showingCountry.image}
          url={AppRoutes.countryRoute({ id: showingCountry.slug }).toUrl()}
          urlLabel={`${translation('explore')} ${showingCountry.name}`}
        />
      ) : null}
    </>
  );
};

export default BlogLayout;
